<template>
  <div class="breadcrumbs--wrapper">
    <v-btn icon @click="handleBack" flat color="transparent">
      <v-icon color="primary"> mdi-chevron-left </v-icon>
    </v-btn>
    <div
      v-for="(item, index) in items"
      :key="item.text"
      class="breadcrumbs--item"
    >
      <span
        :class="[
          'breadcrumbs--item--link',
          item.disabled ? 'breadcrumbs--item--link--disabled' : '',
        ]"
        @click="handelNavigation(item)"
      >
        {{ item.text }}
      </span>
      <span
        v-if="index < items.length - 1"
        class="breadcrumbs--item--separator"
      >
        /
      </span>
    </div>
    <div class="breadcrumbs--spacer"></div>
    <AvatarIconMenu small />
  </div>
</template>

<script lang="ts" setup>
import AvatarIconMenu from "@/components/user/profile/AvatarIconMenu.vue";
import {
  useDashboardNavStore,
  type DashboardDialogContent,
} from "@/stores/dashboardNavStore";
import { computed } from "vue";

type DashboardBreadcrumbsDisplayText =
  | "Dashboard"
  | "Manage account"
  | "Edit account"
  | "My fleet"
  | "Member details"
  | "Fleet locations"
  | "location details"
  | "Fleet vehicles"
  | "Vehicle details"
  | "Edit vehicle details"
  | "Saved trips"
  | "Trip details";

type BreadcrumbsItem = {
  text: DashboardBreadcrumbsDisplayText;
  disabled?: boolean;
  content: DashboardDialogContent;
};

type BreadcrumbsData = {
  items: BreadcrumbsItem[];
  back: DashboardDialogContent | null;
};

const dashboardBreadcrumbs: Record<DashboardDialogContent, BreadcrumbsData> = {
  account: {
    back: "my-fleet",
    items: [
      { text: "Dashboard", content: "my-fleet" },
      { text: "Manage account", content: "account", disabled: true },
    ],
  },

  "account-edit": {
    back: "account",
    items: [
      { text: "Manage account", content: "account"},
      { text: "Edit account", content: "account-edit", disabled: true}
    ]
  },

  "fleet-location-details": {
    back: "fleet-locations",
    items: [
      { text: "My fleet", content: "my-fleet" },
      { text: "Fleet locations", content: "fleet-locations" },
      {
        text: "location details",
        content: "fleet-location-details",
        disabled: true,
      },
    ],
  },

  "fleet-locations": {
    back: "my-fleet",
    items: [
      { text: "My fleet", content: "my-fleet" },
      {
        text: "Fleet locations",
        content: "fleet-locations",
        disabled: true,
      },
    ],
  },

  "fleet-vehicles": {
    back: "my-fleet",
    items: [
      { text: "Dashboard", content: "my-fleet" },
      { text: "My fleet", content: "my-fleet" },
      {
        text: "Fleet vehicles",
        content: "fleet-vehicles",
        disabled: true,
      },
    ],
  },

  "my-fleet": {
    back: "my-fleet",
    items: [
      { text: "My fleet", content: "my-fleet", disabled: true },
    ],
  },

  "fleet-member": {
    back: "my-fleet",
    items: [
      { text: "My fleet", content: "my-fleet" },
      {
        text: "Member details",
        content: "fleet-member",
        disabled: true,
      },
    ],
  },

  "saved-trips": {
    back: "my-fleet",
    items: [
      { text: "My fleet", content: "my-fleet" },
      { text: "Saved trips", content: "saved-trips", disabled: true },
    ],
  },

  "saved-trip-details": {
    back: 'saved-trips',
    items: [
      {
        text: "My fleet", content: 'my-fleet'
      },
      {
        text: "Saved trips", content: "saved-trips"
      },
      {
        text: "Trip details", content: "saved-trip-details", disabled: true
      }
    ]
  },

  "vehicle-details": {
    back: "fleet-vehicles",
    items: [
      { text: "My fleet", content: "my-fleet" },
      { text: "Fleet vehicles", content: "fleet-vehicles" },
      {
        text: "Vehicle details",
        content: "vehicle-details",
        disabled: true,
      },
    ],
  },

  "vehicle-edit-details": {
    back: "vehicle-details",
    items: [
      { text: "My fleet", content: "my-fleet" },
      { text: "Fleet vehicles", content: "fleet-vehicles" },
      { text: "Vehicle details", content: "vehicle-details" },
      {
        text: "Edit vehicle details",
        content: "vehicle-edit-details",
        disabled: true,
      },
    ],
  },

  notifications: {
    back: null,
    items: [],
  },
};

const dashboardNavStore = useDashboardNavStore();

const items = computed(
  () => dashboardBreadcrumbs[dashboardNavStore.displayedContent].items,
);
const backTarget = computed(
  () => dashboardBreadcrumbs[dashboardNavStore.displayedContent].back
)

function handelNavigation(item: BreadcrumbsItem) {
  if (item.disabled) return;
  dashboardNavStore.displayedContent = item.content
}

function handleBack() {
  if (!backTarget.value) return;
  dashboardNavStore.displayedContent = backTarget.value
}
</script>

<style scoped>
.breadcrumbs--wrapper {
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 20px;
  overflow: hidden;
}

.breadcrumbs--item {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  flex-wrap: nowrap;
  min-width: 0;
}

.breadcrumbs--item--separator {
  cursor: default;
  color: rgb(var(--v-theme-gray400 , lightgrey));
  font-weight: bold;
  padding: 0 10px;
}

.breadcrumbs--item--link {
  flex: 1 1 auto;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: 300;
  font-family: "Public Sans", sans-serif;
  min-width: 0;
  padding: 2px 5px;
}

.breadcrumbs--item--link:hover:not(.breadcrumbs--item--link--disabled) {
  background-color: rgb(var(--v-theme-primary20));
}

.breadcrumbs--item--link--disabled {
  flex: 1 0 auto;
  cursor: default;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  font-family: "Public Sans", sans-serif;
  padding: 0 15px;
}

.breadcrumbs--spacer {
  flex: 1 1 auto;
  padding: 0 15px;
}
</style>
